import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Camera, Video, BarChart3, TrendingUp, Users, 
  RefreshCw, ArrowLeft, Settings, Trash2, Eye,
  Crown, Zap, Calendar, AlertCircle
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const AIToolsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [limits, setLimits] = useState(null);
  const [tours, setTours] = useState([]);
  const [videos, setVideos] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingTier, setEditingTier] = useState(null);
  const [tierLimits, setTierLimits] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, limitsRes, toursRes, videosRes] = await Promise.all([
        api.get('/admin/ai-tools/stats'),
        api.get('/admin/ai-tools/default-limits'),
        api.get('/admin/ai-tools/all-tours'),
        api.get('/admin/ai-tools/all-videos')
      ]);
      
      setStats(statsRes.data);
      setLimits(limitsRes.data.tier_limits);
      setTierLimits(limitsRes.data.tier_limits);
      setTours(toursRes.data.tours || []);
      setVideos(videosRes.data.videos || []);
    } catch (error) {
      console.error('Failed to load AI tools data:', error);
      toast.error('Failed to load AI tools data');
    } finally {
      setLoading(false);
    }
  };

  const updateTierLimit = async (tier) => {
    try {
      await api.put(`/api/admin/ai-tools/default-limits/${tier}`, tierLimits[tier]);
      toast.success(`${tier} limits updated`);
      setEditingTier(null);
    } catch (error) {
      toast.error('Failed to update limits');
    }
  };

  const deleteTour = async (tourId) => {
    if (!window.confirm('Delete this virtual tour?')) return;
    try {
      await api.delete(`/api/admin/ai-tools/tour/${tourId}`);
      toast.success('Tour deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tour');
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Delete this video ad?')) return;
    try {
      await api.delete(`/api/admin/ai-tools/video/${videoId}`);
      toast.success('Video deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete video');
    }
  };

  const tierColors = {
    free: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    silver: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    gold: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
    vip: 'bg-purple-500/20 text-purple-400 border-purple-500/30'
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="ai-tools-admin">
      {/* Header */}
      <div className="bg-gradient-to-r from-cyan-500/20 via-background to-purple-500/10 py-8 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Admin
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-gradient-to-r from-cyan-500 to-purple-500 rounded-2xl flex items-center justify-center">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">AI Tools Admin</h1>
                <p className="text-muted-foreground">Manage Virtual Tour & Video Ad creators</p>
              </div>
            </div>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-white/10 rounded-xl flex items-center gap-2 hover:bg-white/20"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-6">
        <div className="flex gap-2 border-b border-border">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'limits', label: 'Tier Limits', icon: Settings },
            { id: 'tours', label: 'All Tours', icon: Camera },
            { id: 'videos', label: 'All Videos', icon: Video }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 flex items-center gap-2 border-b-2 transition-colors ${
                activeTab === tab.id 
                  ? 'border-primary text-primary' 
                  : 'border-transparent text-muted-foreground hover:text-white'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="glass-card p-6 text-center border-l-4 border-cyan-500">
                <Camera className="w-8 h-8 text-cyan-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-cyan-500">{stats?.total?.virtual_tours || 0}</p>
                <p className="text-sm text-muted-foreground">Total Tours</p>
              </div>
              <div className="glass-card p-6 text-center border-l-4 border-purple-500">
                <Video className="w-8 h-8 text-purple-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-purple-500">{stats?.total?.video_ads || 0}</p>
                <p className="text-sm text-muted-foreground">Total Videos</p>
              </div>
              <div className="glass-card p-6 text-center border-l-4 border-green-500">
                <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-green-500">{stats?.monthly?.virtual_tours || 0}</p>
                <p className="text-sm text-muted-foreground">Tours This Month</p>
              </div>
              <div className="glass-card p-6 text-center border-l-4 border-pink-500">
                <TrendingUp className="w-8 h-8 text-pink-500 mx-auto mb-2" />
                <p className="text-3xl font-bold text-pink-500">{stats?.monthly?.video_ads || 0}</p>
                <p className="text-sm text-muted-foreground">Videos This Month</p>
              </div>
            </div>

            {/* Top Users */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="glass-card p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Camera className="w-5 h-5 text-cyan-500" />
                  Top Virtual Tour Creators
                </h3>
                {stats?.top_users?.virtual_tours?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.top_users.virtual_tours.map((user, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            i === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                            i === 1 ? 'bg-gray-400/20 text-gray-400' :
                            i === 2 ? 'bg-orange-500/20 text-orange-500' :
                            'bg-white/10'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="font-medium">{user._id}</span>
                        </div>
                        <span className="text-cyan-500 font-bold">{user.count} tours</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                )}
              </div>

              <div className="glass-card p-6">
                <h3 className="font-bold mb-4 flex items-center gap-2">
                  <Video className="w-5 h-5 text-purple-500" />
                  Top Video Ad Creators
                </h3>
                {stats?.top_users?.video_ads?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.top_users.video_ads.map((user, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            i === 0 ? 'bg-yellow-500/20 text-yellow-500' :
                            i === 1 ? 'bg-gray-400/20 text-gray-400' :
                            i === 2 ? 'bg-orange-500/20 text-orange-500' :
                            'bg-white/10'
                          }`}>
                            {i + 1}
                          </span>
                          <span className="font-medium">{user._id}</span>
                        </div>
                        <span className="text-purple-500 font-bold">{user.count} videos</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">No data yet</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Tier Limits Tab */}
        {activeTab === 'limits' && (
          <div className="space-y-6">
            <div className="glass-card p-6">
              <h3 className="font-bold mb-4 flex items-center gap-2">
                <Crown className="w-5 h-5 text-primary" />
                Tier-Based Usage Limits
              </h3>
              <p className="text-sm text-muted-foreground mb-6">
                Set monthly limits for each membership tier
              </p>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {limits && Object.entries(limits).map(([tier, tierData]) => (
                  <div key={tier} className={`p-4 rounded-xl border ${tierColors[tier]}`}>
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold capitalize">{tier}</h4>
                      <button
                        onClick={() => setEditingTier(editingTier === tier ? null : tier)}
                        className="p-1 hover:bg-white/10 rounded"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>

                    {editingTier === tier ? (
                      <div className="space-y-3">
                        <div>
                          <label className="text-xs text-white">Virtual Tours/Month</label>
                          <input
                            type="number"
                            value={tierLimits[tier]?.virtual_tour_monthly_limit || 0}
                            onChange={(e) => setTierLimits(prev => ({
                              ...prev,
                              [tier]: { ...prev[tier], virtual_tour_monthly_limit: parseInt(e.target.value) }
                            }))}
                            className="w-full mt-1 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-white">Video Ads/Month</label>
                          <input
                            type="number"
                            value={tierLimits[tier]?.video_ad_monthly_limit || 0}
                            onChange={(e) => setTierLimits(prev => ({
                              ...prev,
                              [tier]: { ...prev[tier], video_ad_monthly_limit: parseInt(e.target.value) }
                            }))}
                            className="w-full mt-1 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-white">Max Images/Tour</label>
                          <input
                            type="number"
                            value={tierLimits[tier]?.max_images_per_tour || 0}
                            onChange={(e) => setTierLimits(prev => ({
                              ...prev,
                              [tier]: { ...prev[tier], max_images_per_tour: parseInt(e.target.value) }
                            }))}
                            className="w-full mt-1 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs text-white">Max Video Duration (sec)</label>
                          <input
                            type="number"
                            value={tierLimits[tier]?.max_video_duration || 0}
                            onChange={(e) => setTierLimits(prev => ({
                              ...prev,
                              [tier]: { ...prev[tier], max_video_duration: parseInt(e.target.value) }
                            }))}
                            className="w-full mt-1 px-3 py-2 bg-slate-900 text-white border border-slate-600 rounded-lg focus:ring-2 focus:ring-primary"
                          />
                        </div>
                        <button
                          onClick={() => updateTierLimit(tier)}
                          className="btn-primary w-full mt-2"
                        >
                          Save
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Tours/Month</span>
                          <span className="font-semibold">{tierData.virtual_tour_monthly_limit}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Videos/Month</span>
                          <span className="font-semibold">{tierData.video_ad_monthly_limit}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Max Images</span>
                          <span className="font-semibold">{tierData.max_images_per_tour}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Max Duration</span>
                          <span className="font-semibold">{tierData.max_video_duration}s</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* All Tours Tab */}
        {activeTab === 'tours' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">All Virtual Tours ({tours.length})</h2>
            </div>

            {tours.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Camera className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No tours yet</h3>
                <p className="text-muted-foreground">Virtual tours will appear here once users create them.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tours.map(tour => (
                  <div key={tour.id} className="glass-card overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-muted-foreground" />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{tour.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        by {tour.user_id} • {tour.original_images?.length || 0} images
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {new Date(tour.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2">
                        <button className="btn-secondary flex-1 flex items-center justify-center gap-1">
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button 
                          onClick={() => deleteTour(tour.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold">All Video Ads ({videos.length})</h2>
            </div>

            {videos.length === 0 ? (
              <div className="glass-card p-12 text-center">
                <Video className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">No video ads yet</h3>
                <p className="text-muted-foreground">Video ads will appear here once users create them.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map(video => (
                  <div key={video.id} className="glass-card overflow-hidden">
                    <div className="aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center relative">
                      <Video className="w-12 h-12 text-muted-foreground" />
                      <span className="absolute top-2 right-2 bg-black/70 px-2 py-1 rounded text-xs">
                        {video.duration}s
                      </span>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold mb-1 truncate">{video.name}</h3>
                      <p className="text-sm text-muted-foreground mb-2">
                        by {video.user_id} • {video.style} style
                      </p>
                      <p className="text-xs text-muted-foreground mb-3">
                        {new Date(video.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex gap-2">
                        <button className="btn-secondary flex-1 flex items-center justify-center gap-1">
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                        <button 
                          onClick={() => deleteVideo(video.id)}
                          className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIToolsAdmin;
